import { useFormik } from "formik";
import debounce from "lodash.debounce";
import fileDownload from "js-file-download";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import { Box, Button, TextField } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import Title from "../../common/Title";
import InputFieldAutoComplete from "../../common/InputFieldAutoComplete";

import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import useAlert from "../../../hooks/useAlert";
import { __getGroups } from "../../../api/group";
import { __getUserList } from "../../../api/user";
import { __assignMdns, __exportMdns, __getMdns } from "../../../api/mdn";

const MDNAlias = () => {
  const { user } = useAuth();
  const nav = useNavigate();

  if (!user.sub_user) {
    nav("/mdn");
  }

  const [userResponse, userCall, userApiLoading] = useAxios(__getUserList);
  const [groupResponse, groupCall, groupApiLoading] = useAxios(__getGroups);
  const [exportResponse, exportCall, exportLoading] = useAxios({
    ...__exportMdns,
  });
  const { setAlertMessage } = useAlert();
  const [groups, setGroups] = useState([]);
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [currentGroup, setCurrentGroup] = useState();
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState(null);
  const [response, call, loading] = useAxios({
    ...__assignMdns,
  });
  const initialData = { lines: [], user_id: null };

  const [mdns, setMdns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [metaData, setMetaData] = useState({
    page: 1,
    page_size: 100,
    total: 1,
  });
  const [sortQuery, setSortQuery] = useState();
  const [mdnResponse, mdnCall, mdnLoading] = useAxios({ ...__getMdns });

  const debouncedFetchData = debounce((query) => {
    mdnCall(query);
  }, 500);

  useEffect(() => {
    debouncedFetchData({
      params: {
        page: currentPage,
        page_size: pageSize,
        sort: sortQuery,
        group_id: user.id !== 1 ? user.group_id : null,
        show_user: true,
      },
    });
    return () => {
      debouncedFetchData.cancel();
    };
  }, [currentPage, pageSize, sortQuery]);

  useEffect(() => {
    if (mdnResponse && mdnResponse.status) {
      setMetaData(mdnResponse.data.meta);
      setMdns(mdnResponse.data.data);
    }
  }, [mdnResponse]);

  useEffect(() => {
    if (exportResponse && exportResponse.status) {
      fileDownload(exportResponse.data, "mdn_export.csv");
    }
  }, [exportResponse]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setCurrentPage(1);
    setPageSize(newPageSize);
  };

  useEffect(() => {
    if (formData) {
      call({
        data: { ...formData, lines: linesData },
      });
    }
  }, [formData]);

  useEffect(() => {
    formik.setSubmitting(false);
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "Updated sims" });
      nav(`/mdn`);
    }
    validateFunction(rawData);
  }, [response]);

  useEffect(() => {
    if (!user.admin) return;
    userCall({ params: { page_size: 1000 } });
    groupCall({ params: { page_size: 1000 } });
  }, []);

  useEffect(() => {
    if (userResponse && userResponse.status) {
      setUsers(userResponse.data.data);
    }
  }, [userResponse]);

  useEffect(() => {
    if (groupResponse && groupResponse.status) {
      setGroups(groupResponse.data.data);
      groupResponse.data.data.length === 1 &&
        setCurrentGroup(groupResponse.data.data[0]);
    }
  }, [groupResponse]);

  useEffect(() => {
    formik.setErrors(validateFunction(rawData));
  }, [mdns, rawData]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];
    if (!data) {
      return formik.setErrors(errors);
    }

    const lines = data.split("\n");
    lines.some((line, idx) => {
      const data = line.split(",");
      if (1 > data.length > 2) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }
      try {
        const line_data = line.split(",");
        const obtainedData =
          line_data.length === 1
            ? { line: line_data[0].trim(), nickname: null }
            : {
                line: line_data[0].trim(),
                nickname: line_data[1].trim() || null,
              };

        if (isNaN(obtainedData.line.trim())) {
          errors.lines = `Invalid number in line ${idx + 1}: ${line}`;
          return true;
        }
        if (
          obtainedData.line.trim().length !== 10 &&
          (obtainedData.line.trim().length > 20 ||
            obtainedData.line.trim().length < 19)
        ) {
          throw new Error(`Invalid number in line ${idx + 1}: ${line}`);
        }
        formatedLines.push(obtainedData);
      } catch (err) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    } else {
      setLinesData([]);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        obj[key] = values[key] || null;
        return obj;
      }, {});

      setFormData(requestData);
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };
  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    // Check if user has entered the file
    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors?.lines,
          upload: "Please input a csv file",
        });
        return;
      }

      handleParse(inputFile);
    }
  };

  if (loading || userApiLoading || groupApiLoading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%", mb: 5 }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content={user.admin ? "Assign/Update SIMs to users" : "Update alias"}
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
          maxWidth={350}
        >
          {user.admin && (
            <>
              <Grid item xs={12} minWidth={"100%"}>
                <InputFieldAutoComplete
                  isSelect
                  options={[
                    ...groups.map((group) => ({
                      ...group,
                      label: `${group.group_name}`,
                    })),
                  ]}
                  name="group"
                  label="Group"
                  id="group"
                  disabled={groups.length === 1}
                  value={currentGroup?.group_name}
                  isOptionEqualToValue={(option, value) =>
                    option && value && option.id === value.id
                  }
                  onChange={(e, value) => setCurrentGroup(value)}
                />
              </Grid>
              <Grid item xs={12} minWidth={"100%"}>
                <FormControl
                  error={
                    formik.touched.user_id && Boolean(formik.errors?.user_id)
                      ? true
                      : false
                  }
                  sx={{ width: "100%" }}
                >
                  <InputFieldAutoComplete
                    isSelect
                    options={[
                      ...users.reduce((acc, user) => {
                        if (user.group_id !== currentGroup?.id) {
                          return acc;
                        }
                        const val = {
                          value: user.id,
                          label: `${user.username}${user.admin ? "*" : ""}`,
                          group_id: user.group_id,
                        };
                        acc.push(val);
                        return acc;
                      }, []),
                    ]}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    name="user_id"
                    label="User"
                    id="user_id"
                    onChange={(e, value) =>
                      formik.setFieldValue("user_id", value?.value || null)
                    }
                  />
                  {formik.touched.user_id && Boolean(formik.errors?.user_id) ? (
                    <FormHelperText id="user_id-error-text">
                      {formik.errors?.user_id}
                    </FormHelperText>
                  ) : (
                    ""
                  )}
                </FormControl>
              </Grid>
            </>
          )}
          <Grid item xs={12} minWidth={"100%"}>
            <FormControl
              error={Boolean(formik.errors?.upload) ? true : false}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors?.upload) ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors?.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={"100%"}>
            <InputLabel htmlFor="lines">
              {"Add in the format. "}
              <em>{"Alias is optional."}</em>
              <br></br>
              {"ICCID/MDN,[Alias]"}
              <br></br>
              {"ICCID/MDN,[Alias]"}
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors?.lines) ? true : false}
              sx={{ width: "100%" }}
            >
              <TextField
                multiline
                rows={6}
                defaultValue="Default Value"
                aria-label="minimum height"
                placeholder={`Add in the format\nICCID/MDN,[Alias]\nICCID/MDN,[Alias]`}
                name="lines"
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                }}
              />
              {Boolean(formik.errors?.lines) ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors?.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <LoadingButton
                disabled={
                  !linesData || !linesData.length || formik.errors?.lines
                }
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Update SIMs"}
              </LoadingButton>
            </Grid>
            <Grid item>
              <LoadingButton
                size="large"
                onClick={() => nav("/mdn")}
                loading={loading}
                variant="contained"
              >
                Cancel
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default MDNAlias;
